import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ProfileImageDrawer.css';

import takePhotoIcon from '../../assets/takephoto-icon.png';
import galleryIcon from '../../assets/choosefromgallary-icon.png';
import studioIcon from '../../assets/choosefromstudio-icon.png';
import removeIcon from '../../assets/removeimages-icon.png';

import { canAccessStudio } from '../../studio/studioAccess';


function ProfileImageDrawer({
  isOpen,
  type = 'avatar',
  canRemove = false,
  busy = false,
  error = '',
  onClose,
  onTakePhoto,
  onChooseGallery,

  onChooseStudio,
  onCreateAvatar,
  user,
  onRemove,
}) {
  const sheetRef = useRef(null);
  const closeRef = useRef(onClose);
  const busyRef = useRef(busy);

  closeRef.current = onClose;
  busyRef.current = busy;

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';
    sheetRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();

        if (!busyRef.current) {
          closeRef.current?.();
        }
      }

      if (event.key === 'Tab') {
        const buttons = Array.from(
          sheetRef.current?.querySelectorAll(
            'button:not(:disabled)'
          ) || []
        );

        if (!buttons.length) {
          event.preventDefault();
          sheetRef.current?.focus();
          return;
        }

        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && (active === first || !buttons.includes(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !buttons.includes(active))) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);

      if (previousFocus?.isConnected) {
        previousFocus.focus?.();
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isBanner = type === 'banner';

  const options = isBanner
    ? [
        {
          label: 'Choose from Studio',
          icon: studioIcon,
          action: onChooseStudio,
        },
      ]
    : [
        {
          label: 'Take photo',
          icon: takePhotoIcon,
          action: onTakePhoto,
        },
        {
          label: 'Choose from gallery',
          icon: galleryIcon,
          action: onChooseGallery,
        },
      ];



  if (
    !isBanner &&
    canAccessStudio(user) &&
    typeof onCreateAvatar === 'function'
  ) {
    options.push({
      label: 'Create your Avatar',
      icon: studioIcon,
      action: onCreateAvatar,
    });
  }


  return createPortal(
    <div
      className="pid-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onClose?.();
        }
      }}
    >
      <div
        ref={sheetRef}
        className="pid-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={isBanner ? 'Banner options' : 'Profile photo options'}
        aria-busy={busy}
        tabIndex={-1}
      >
        <div className="pid-grabber" aria-hidden="true" />

        {options.map((option) => (
          <button
            key={option.label}
            type="button"
            className="pid-item"
            onClick={option.action}
            disabled={busy}
          >
            <img
              src={option.icon}
              alt=""
              className="pid-item-icon"
            />
            <span>{option.label}</span>
          </button>
        ))}

        {canRemove && (
          <button
            type="button"
            className="pid-item pid-danger"
            onClick={onRemove}
            disabled={busy}
          >
            <img
              src={removeIcon}
              alt=""
              className="pid-item-icon"
            />
            <span>
              {busy
                ? 'Removing…'
                : isBanner
                  ? 'Remove banner'
                  : 'Remove photo'}
            </span>
          </button>
        )}

        {error && (
          <p className="pid-error" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="pid-cancel"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body
  );
}

export default ProfileImageDrawer;
